import React, { useState, useEffect } from 'react';
import {
  SiPython, SiDjango, SiReact, SiJavascript, SiDocker,
  SiKubernetes, SiApachekafka, SiPostgresql,
  SiMongodb, SiGit, SiJenkins, SiFirebase, SiHtml5,
  SiMysql, SiNodedotjs, SiGraphql
} from 'react-icons/si';
import { FaAws } from 'react-icons/fa';

const GuiDashboard = ({ setView }) => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState({ isSubmitting: false, isSubmitted: false, isError: false, message: '' });
  const [expandedJob, setExpandedJob] = useState(null);
  const [location, setLocation] = useState({ city: 'Detecting...', status: 'LOCATING' });
  const [skillFilter, setSkillFilter] = useState('All');

  // IP-based geolocation (no permission needed)
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('https://ipapi.co/json/');
        const data = await resp.json();
        const city = data.city || 'Unknown';
        const country = data.country_code || '';
        setLocation({ city: `${city}, ${country}`, status: 'LIVE' });
      } catch {
        setLocation({ city: 'Unknown Location', status: 'OFFLINE' });
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ isSubmitting: true, isSubmitted: false, isError: false, message: '' });
    try {
      const response = await fetch('/api/sendEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send message');
      setFormStatus({ isSubmitting: false, isSubmitted: true, isError: false, message: data.message || 'Message sent!' });
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setFormStatus(prev => ({ ...prev, isSubmitted: false })), 5000);
    } catch (error) {
      setFormStatus({ isSubmitting: false, isSubmitted: false, isError: true, message: error.message || 'Failed to send. Please try again.' });
    }
  };

  const jobs = [
    {
      id: 'certa',
      period: 'Dec 2024 — Present',
      title: 'Software Development Engineer - III',
      company: 'Certa.ai · Delhi, IN',
      summary: 'Building centralized microservices and auth infrastructure for enterprise governance workflows.',
      highlights: [
        'Architected a centralized comments microservice with REST APIs achieving 99.9% availability and <100ms latency.',
        'Developed a shared auth package adopted by 15+ microservices, reducing duplicate code by 80%.',
      ],
      extra: [
        'Led SSO redesign with a transformational rules engine for flexible IDP attribute mapping across platforms.',
      ],
      tags: ['PYTHON', 'DJANGO', 'MICROSERVICES', 'REST', 'SSO'],
    },
    {
      id: 'spinny',
      period: 'Aug 2021 — Dec 2024',
      title: 'Senior Software Engineer',
      company: 'Spinny · Gurgaon, IN',
      summary: 'Led infrastructure optimization, microservices migration, and team of engineers for 3+ years.',
      highlights: [
        '60% infra cost reduction via Kubernetes migration with consistent performance.',
        'Reduced memory utilization from 95% to 10% through improved caching and NoSQL migration.',
        'Optimized APIs decreasing response times by 60% through reduced DB calls and caching.',
      ],
      extra: [
        'Halved time to procure a car, improving operational efficiency and customer satisfaction.',
        'Enhanced inspection accuracy by 25% via streamlined checklists.',
        'Reduced car inspection duration by 20% (60 → 48 min), increasing daily inspection count.',
        'Developed alerting system reducing business loss by 30% through early error identification.',
        'Enhanced scheduler capacity: 1,200 → 4,000 daily assignments.',
        'Achieved 99.9% service uptime via microservices architecture.',
        'Designed inspection data API: 60ms response, 2,000+ RPM, 40% stability improvement.',
        'Revamped loan report workflows: 15% increase in loan acceptance, 50% faster API response.',
        'Lead Developer for 2+ years, guiding the engineering team.',
      ],
      tags: ['PYTHON', 'DJANGO', 'K8S', 'AWS', 'KAFKA', 'DOCKER'],
    },
    {
      id: 'mckinley',
      period: 'Aug 2020 — Aug 2021',
      title: 'Full Stack Developer',
      company: 'McKinley & Rice · Noida, IN',
      summary: 'Built full-stack products from the ground up, including mobile apps and web platforms.',
      highlights: [
        'Developed Rexpert, an expert-driven platform for enhancing resumes and connecting with recruiters.',
        'Led development of EmergApp, an Employee Management System with an integrated SOS feature.',
      ],
      extra: [],
      tags: ['REACT', 'REACT NATIVE', 'NODE.JS', 'FIREBASE'],
    },
  ];

  const allSkills = [
    { name: 'Python',       cat: 'Language',  Icon: SiPython },
    { name: 'JavaScript',   cat: 'Language',  Icon: SiJavascript },
    { name: 'SQL',          cat: 'Language',  Icon: SiMysql },
    { name: 'HTML/CSS',     cat: 'Frontend',  Icon: SiHtml5 },
    { name: 'Django',       cat: 'Framework', Icon: SiDjango },
    { name: 'React',        cat: 'Frontend',  Icon: SiReact },
    { name: 'React Native', cat: 'Mobile',    Icon: SiReact },
    { name: 'Node.js',      cat: 'Framework', Icon: SiNodedotjs },
    { name: 'REST / gRPC',  cat: 'Framework', Icon: SiGraphql },
    { name: 'Docker',       cat: 'DevOps',    Icon: SiDocker },
    { name: 'Kubernetes',   cat: 'DevOps',    Icon: SiKubernetes },
    { name: 'Git',          cat: 'DevOps',    Icon: SiGit },
    { name: 'Jenkins',      cat: 'DevOps',    Icon: SiJenkins },
    { name: 'AWS',          cat: 'Cloud',     Icon: FaAws },
    { name: 'Kafka',        cat: 'Cloud',     Icon: SiApachekafka },
    { name: 'Firebase',     cat: 'Cloud',     Icon: SiFirebase },
    { name: 'PostgreSQL',   cat: 'Database',  Icon: SiPostgresql },
    { name: 'MongoDB',      cat: 'Database',  Icon: SiMongodb },
  ];

  const categories = ['All', ...Array.from(new Set(allSkills.map(s => s.cat)))];
  const filteredSkills = skillFilter === 'All' ? allSkills : allSkills.filter(s => s.cat === skillFilter);

  const projects = [
    {
      title: 'Home NAS Server',
      description: 'Self-hosted network-attached storage on TrueNAS with RAID-Z2, serving 14TB+ data with automated backups and snapshot management.',
      tags: ['TRUENAS', 'ZFS', 'RAID-Z2', 'NETWORKING'],
      icon: 'dns',
      status: 'ONLINE',
    },
    {
      title: 'Media Streaming Server',
      description: 'Personal Plex media server with automated content management, hardware transcoding, and remote-access streaming for concurrent sessions.',
      tags: ['PLEX', 'DOCKER', 'FFMPEG', 'NGINX'],
      icon: 'movie',
      status: 'ACTIVE',
    },
    {
      title: 'Portfolio Website',
      description: 'This dual-mode portfolio (GUI + Terminal) built with React and Tailwind CSS, featuring dark/light themes and an interactive CLI.',
      tags: ['REACT', 'TAILWIND', 'CSS VARS', 'JSX'],
      icon: 'web',
      status: 'LIVE',
    },
    {
      title: 'Home Automation Hub',
      description: 'Custom home automation with smart device integration, energy monitoring dashboards, and scheduled routines.',
      tags: ['IOT', 'DOCKER', 'MQTT', 'GRAFANA'],
      icon: 'home',
      status: 'RUNNING',
    },
  ];

  return (
    <main className="pb-24 px-4 md:px-8 pt-8 md:pt-12 max-w-7xl mx-auto font-body selection:bg-primary selection:text-on-primary">
      
      {/* Hero / About */}
      <section className="mb-16 md:mb-32 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start" id="about">
        <div className="lg:col-span-8">
          <div className="mb-4 font-['Space_Grotesk'] text-secondary tracking-widest uppercase text-xs">{'/* 01. About Me */'}</div>
          <h1 className="font-headline font-black text-4xl md:text-6xl lg:text-7xl text-on-surface tracking-tighter leading-none mb-8">
            BUILDING SCALABLE <span style={{ WebkitTextStroke: '1px var(--color-outline-variant)', color: 'transparent' }}>SOFTWARE</span>
          </h1>
          <p className="text-on-surface-variant text-lg md:text-xl max-w-2xl leading-relaxed">
            Software Engineer with ~6 years of experience driving technological innovation. Specializing in microservices architecture, API optimizations, and cloud computing to deliver high-quality, scalable solutions.
          </p>
        </div>
        <div className="lg:col-span-4 bg-surface-container-low p-6 rounded border border-outline-variant/10 transition-colors duration-300">
          <div className="flex items-center justify-between mb-6">
            <span className="text-xs font-mono text-on-surface-variant/50">VISITOR LOCATION</span>
            <span className={`text-xs font-mono ${location.status === 'LIVE' ? 'text-emerald-500' : 'text-on-surface-variant/60'} ${location.status === 'LOCATING' ? 'animate-pulse' : ''}`}>
              ● {location.status}
            </span>
          </div>
          <div className="aspect-video bg-surface-container-lowest overflow-hidden rounded relative border border-outline-variant/20">
            <div className="absolute inset-0 bg-gradient-to-br from-surface-container-low to-surface-container-lowest"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full px-4 text-center">
                <span className="material-symbols-outlined text-4xl text-on-surface-variant/30 mb-2 block">location_on</span>
                <div className="text-xl md:text-2xl font-black text-on-surface tracking-tighter">{location.city}</div>
                <div className="text-[10px] font-mono text-on-surface-variant/60 mt-1">
                  {new Date().toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })} IST
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="mb-16 md:mb-32">
        <div className="mb-8 flex items-baseline justify-between border-b border-outline-variant/10 pb-4">
          <h2 className="font-headline font-extrabold text-2xl md:text-3xl text-on-surface tracking-tight uppercase">{'// Key Stats'}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { icon: 'speed', value: '60%', label: 'Infra Cost Saved' },
            { icon: 'memory', value: '95→10%', label: 'Memory Optimized' },
            { icon: 'trending_up', value: '99.9%', label: 'Service Uptime' },
            { icon: 'api', value: '<100ms', label: 'API Latency' },
          ].map((stat) => (
            <div key={stat.label} className="bg-surface-container-low p-4 md:p-5 rounded border border-outline-variant/10 hover:bg-surface-container-high transition-colors">
              <span className="material-symbols-outlined text-secondary mb-3 block">{stat.icon}</span>
              <div className="text-2xl md:text-3xl font-black text-on-surface mb-1">{stat.value}</div>
              <div className="text-[11px] text-on-surface-variant font-mono">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Experience — Collapsible */}
      <section className="mb-16 md:mb-32" id="experience">
        <div className="mb-12">
          <div className="text-xs font-mono text-on-surface-variant/50 mb-2 uppercase tracking-widest">{'/* 02. Work Experience */'}</div>
          <h2 className="font-headline font-black text-3xl md:text-5xl text-on-surface tracking-tight">Timeline</h2>
        </div>
        <div className="space-y-0.5">
          {jobs.map((job) => {
            const isExpanded = expandedJob === job.id;
            return (
              <div key={job.id} className="group relative bg-surface-container-low hover:bg-surface-container-high transition-all border-l-2 border-transparent hover:border-primary">
                {/* Collapsed summary — always visible */}
                <div 
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 p-6 md:p-8 cursor-pointer"
                  onClick={() => setExpandedJob(isExpanded ? null : job.id)}
                >
                  <div className="md:col-span-3 text-xs font-mono text-on-surface-variant uppercase pt-1">
                    {job.period}
                  </div>
                  <div className="md:col-span-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg md:text-xl font-bold text-on-surface">{job.title}</h3>
                        <p className="text-secondary font-medium mb-2">{job.company}</p>
                      </div>
                      <span className="material-symbols-outlined text-on-surface-variant/50 transition-transform duration-300 shrink-0 ml-4" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                        expand_more
                      </span>
                    </div>
                    {!isExpanded && (
                      <p className="text-on-surface-variant text-sm font-light italic">{job.summary}</p>
                    )}
                  </div>
                  <div className="md:col-span-3 flex flex-wrap gap-2 content-start">
                    {job.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 text-[11px] font-mono bg-surface-container-lowest border border-outline-variant/20 rounded text-on-surface-variant">{tag}</span>
                    ))}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-6 md:px-8 pb-6 md:pb-8 pt-0 md:pl-8 md:ml-[25%] animate-[fadeIn_0.3s_ease]">
                    <ul className="text-on-surface-variant text-sm space-y-2 font-light list-none">
                      {job.highlights.map((point, i) => (
                        <li key={i} className="flex items-start"><span className="mr-2 text-primary shrink-0">→</span> {point}</li>
                      ))}
                      {job.extra.map((point, i) => (
                        <li key={`e-${i}`} className="flex items-start"><span className="mr-2 text-primary shrink-0">→</span> {point}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Skills with Icons & Filters */}
      <section className="mb-16 md:mb-32" id="skills">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="text-xs font-mono text-on-surface-variant/50 mb-2 uppercase tracking-widest">{'/* 03. Technologies */'}</div>
            <h2 className="font-headline font-black text-3xl md:text-5xl text-on-surface tracking-tight">Skills</h2>
          </div>
          <div className="text-sm text-on-surface-variant">{filteredSkills.length} technologies</div>
        </div>
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSkillFilter(cat)}
              className={`px-3 py-1.5 text-xs font-mono rounded border transition-all ${
                skillFilter === cat 
                  ? 'bg-primary text-on-primary border-primary' 
                  : 'bg-surface-container-low text-on-surface-variant border-outline-variant/20 hover:bg-surface-container-high'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0.5 bg-outline-variant/10 border border-outline-variant/10 overflow-hidden">
          {filteredSkills.map((s) => (
            <div key={s.name} className="bg-surface p-5 md:p-7 group hover:bg-surface-container-high transition-all">
              <div className="text-on-surface-variant group-hover:text-on-surface transition-colors">
                <s.Icon className="text-3xl mb-4 block opacity-40 group-hover:opacity-100 transition-all group-hover:scale-110" />
                <div className="font-bold text-base md:text-lg mb-1 uppercase tracking-tighter">{s.name}</div>
                <div className="text-[11px] font-mono text-on-surface-variant/50">{s.cat}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Personal Projects */}
      <section className="mb-16 md:mb-32" id="projects">
        <div className="mb-12 border-b border-outline-variant/10 pb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="text-xs font-mono text-on-surface-variant/50 mb-2 uppercase tracking-widest">{'/* 04. Projects */'}</div>
            <h2 className="font-headline font-black text-3xl md:text-5xl text-on-surface tracking-tight">Personal Projects</h2>
          </div>
          <a href="https://github.com/nomitv" target="_blank" rel="noreferrer" className="font-mono text-[10px] text-on-surface-variant hover:text-on-surface transition-colors flex items-center">
            VIEW GITHUB <span className="material-symbols-outlined text-sm ml-2">open_in_new</span>
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {projects.map((proj) => (
            <div key={proj.title} className="group bg-surface-container-low border border-outline-variant/10 rounded-sm p-6 md:p-8 hover:border-primary/30 transition-all flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <span className="material-symbols-outlined text-2xl text-secondary group-hover:text-on-surface transition-colors">{proj.icon}</span>
                <span className="text-[11px] font-mono px-2.5 py-0.5 bg-emerald-500/10 text-emerald-500 rounded">{proj.status}</span>
              </div>
              <h4 className="text-lg font-bold text-on-surface mb-2 tracking-tight">{proj.title}</h4>
              <p className="text-on-surface-variant text-sm font-light mb-6 leading-relaxed flex-grow">{proj.description}</p>
              <div className="flex flex-wrap gap-2">
                {proj.tags.map((tag) => (
                  <span key={tag} className="text-[11px] font-mono text-on-surface-variant/60 bg-surface-container-lowest px-2 py-0.5 rounded border border-outline-variant/10">{tag}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="mb-16 md:mb-32" id="contact">
        <div className="bg-surface-container-low p-6 md:p-12 rounded-sm border border-outline-variant/20 relative overflow-hidden">
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <div>
              <div className="text-xs font-mono text-on-surface-variant/50 mb-4 uppercase tracking-widest">{'/* 05. Contact Me */'}</div>
              <h2 className="font-headline font-black text-3xl md:text-5xl text-on-surface tracking-tight mb-6 leading-tight">Let's work together.</h2>
              <p className="text-on-surface-variant mb-8 font-light">Available for full-stack development, architecture consultations, and infrastructure optimization.</p>
              
              <div className="space-y-4">
                <a href="mailto:nomitverma1@gmail.com" className="flex items-center space-x-4 group cursor-pointer text-on-surface hover:text-primary transition-colors">
                  <div className="w-10 h-10 bg-surface-container-lowest flex items-center justify-center border border-outline-variant/20 group-hover:border-primary transition-colors rounded">
                    <span className="material-symbols-outlined text-sm">mail</span>
                  </div>
                  <div>
                    <div className="text-[9px] font-mono text-on-surface-variant/50">EMAIL</div>
                    <div className="text-sm font-bold">nomitverma1@gmail.com</div>
                  </div>
                </a>
                <a href="https://www.linkedin.com/in/nomit-verma/" target="_blank" rel="noreferrer" className="flex items-center space-x-4 group cursor-pointer text-on-surface hover:text-primary transition-colors">
                  <div className="w-10 h-10 bg-surface-container-lowest flex items-center justify-center border border-outline-variant/20 group-hover:border-primary transition-colors rounded">
                    <span className="material-symbols-outlined text-sm">person</span>
                  </div>
                  <div>
                    <div className="text-[9px] font-mono text-on-surface-variant/50">LINKEDIN</div>
                    <div className="text-sm font-bold">linkedin.com/in/nomit-verma</div>
                  </div>
                </a>
                <a href="https://github.com/nomitv" target="_blank" rel="noreferrer" className="flex items-center space-x-4 group cursor-pointer text-on-surface hover:text-primary transition-colors">
                  <div className="w-10 h-10 bg-surface-container-lowest flex items-center justify-center border border-outline-variant/20 group-hover:border-primary transition-colors rounded">
                    <span className="material-symbols-outlined text-sm">code</span>
                  </div>
                  <div>
                    <div className="text-[9px] font-mono text-on-surface-variant/50">GITHUB</div>
                    <div className="text-sm font-bold">github.com/nomitv</div>
                  </div>
                </a>
              </div>
            </div>
            
            <div className="bg-surface-container-lowest p-6 md:p-8 rounded-sm border border-outline-variant/10 relative z-10">
              {formStatus.isSubmitted ? (
                <div className="bg-emerald-500/10 border border-emerald-500/50 text-emerald-600 dark:text-emerald-400 px-4 py-3 rounded-sm mb-4 text-xs font-mono">
                  <strong className="font-bold border-r border-emerald-500/30 pr-2 mr-2">SUCCESS</strong>
                  <span>{formStatus.message || 'Message sent!'}</span>
                </div>
              ) : formStatus.isError ? (
                <div className="bg-error/10 border border-error/50 text-error px-4 py-3 rounded-sm mb-4 text-xs font-mono">
                  <strong className="font-bold pr-2 mr-2">ERROR</strong>
                  <span>{formStatus.message || 'Failed to send. Please try again.'}</span>
                </div>
              ) : null}

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-mono text-on-surface-variant/50 mb-2 uppercase">Name</label>
                    <input
                      name="name" value={formData.name} onChange={handleChange} required
                      className="w-full bg-surface-container-low border border-outline-variant/10 focus:ring-1 focus:ring-primary text-sm text-on-surface p-3 rounded-sm placeholder:text-on-surface-variant/30 outline-none"
                      placeholder="Your Name" type="text"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono text-on-surface-variant/50 mb-2 uppercase">Email</label>
                    <input
                      name="email" value={formData.email} onChange={handleChange} required
                      className="w-full bg-surface-container-low border border-outline-variant/10 focus:ring-1 focus:ring-primary text-sm text-on-surface p-3 rounded-sm placeholder:text-on-surface-variant/30 outline-none"
                      placeholder="you@example.com" type="email"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-mono text-on-surface-variant/50 mb-2 uppercase">Subject</label>
                  <input
                    name="subject" value={formData.subject} onChange={handleChange} required
                    className="w-full bg-surface-container-low border border-outline-variant/10 focus:ring-1 focus:ring-primary text-sm text-on-surface p-3 rounded-sm placeholder:text-on-surface-variant/30 outline-none"
                    placeholder="Project Inquiry" type="text"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono text-on-surface-variant/50 mb-2 uppercase">Message</label>
                  <textarea
                    name="message" value={formData.message} onChange={handleChange} required
                    className="w-full bg-surface-container-low border border-outline-variant/10 focus:ring-1 focus:ring-primary text-sm text-on-surface p-3 rounded-sm placeholder:text-on-surface-variant/30 outline-none"
                    placeholder="Tell me about your project..." rows="4"
                  ></textarea>
                </div>
                <button
                  disabled={formStatus.isSubmitting}
                  className="w-full bg-primary text-on-primary font-bold py-3 px-6 rounded-sm uppercase tracking-widest text-xs flex items-center justify-center hover:opacity-90 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-wait"
                  type="submit"
                >
                  {formStatus.isSubmitting ? 'SENDING...' : 'SEND MESSAGE'} <span className="material-symbols-outlined ml-2 text-sm">send</span>
                </button>
              </form>
            </div>
          </div>
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-primary/5 blur-[120px] pointer-events-none z-0"></div>
        </div>
      </section>
    </main>
  );
};

export default GuiDashboard;
